import React from "react";
import { useSkin } from "../../skins/SkinContext";
import { SkCircle, SkPolygon } from "../../prim";
import { skPerson, skSun } from "../../actors3";
import { skChild, skTeacher } from "../../actors3/characters";
import { skStar } from "../../actors3/nature";
import { skLightbulb } from "../../actors3/objects";
import type { SkinSceneProps } from "./SkinParkScene";

/*
 * Anivaze v3 — skin-aware "frame" scenes (title + recap) used to bookend an
 * all-skin lesson so the WHOLE video re-skins, not just the concept scene.
 */

function twoLines(label: string): [string, string] {
  if (label.length <= 22) return [label, ""];
  const words = label.split(" ");
  let a = "";
  let i = 0;
  while (i < words.length && (a + " " + words[i]).trim().length <= 22) {
    a = (a + " " + words[i]).trim();
    i++;
  }
  return [a, words.slice(i).join(" ")];
}

/** Wrap a key phrase into up to 3 lines (~26 chars) for the explainer board. */
function wrapLines(label: string, max = 26, maxLines = 3): string[] {
  const words = label.split(" ");
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length <= max) cur = (cur + " " + w).trim();
    else { if (cur) lines.push(cur); cur = w; if (lines.length >= maxLines - 1) break; }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  return lines.length ? lines : [label];
}

export const SkinTitleScene: React.FC<SkinSceneProps> = ({ opacity = 1, t = 0, label = "Let's learn something fun!" }) => {
  const skin = useSkin();
  const [l1, l2] = twoLines(label);
  return (
    <g opacity={opacity}>
      <rect width={800} height={450} fill={skin.surface} />
      {skSun(700, 70, t, 0.9)}
      {/* decorative twinkles */}
      {[140, 300, 520, 660].map((x, i) => <g key={i}>{skStar(x, 70 + (i % 2) * 24, t, 0.5)}</g>)}
      {/* title card */}
      <g transform="translate(400 200)">
        <rect x={-320} y={-58} width={640} height={l2 ? 120 : 86} rx={24} fill="rgba(255,255,255,0.9)" stroke="#cbd5e1" strokeWidth={2} />
        <text x={0} y={l2 ? -8 : 12} textAnchor="middle" fill={skin.textInk} fontSize={34} fontWeight={800} fontFamily={skin.fontFamily}>{l1}</text>
        {l2 ? <text x={0} y={36} textAnchor="middle" fill={skin.textInk} fontSize={34} fontWeight={800} fontFamily={skin.fontFamily}>{l2}</text> : null}
      </g>
      {skChild(400, 400, t, 1.7, true)}
    </g>
  );
};

/** Generic skin-aware presenter scene for ANY topic: a teacher at a board showing
 * the key phrase, with the avatar narrating the real explanation. */
export const SkinExplainerScene: React.FC<SkinSceneProps> = ({ opacity = 1, t = 0, label = "Here's the key idea" }) => {
  const skin = useSkin();
  const lines = wrapLines(label);
  const lh = 30;
  return (
    <g opacity={opacity}>
      <rect width={800} height={450} fill={skin.surface} />
      {skSun(710, 70, t, 0.7)}
      {skStar(120, 96, t, 0.6)}
      {skLightbulb(660, 150, t, 0.8)}
      {/* the board */}
      <g transform="translate(440 200)">
        <rect x={-300} y={-110} width={600} height={220} rx={18} fill="#ffffff" stroke="#94a3b8" strokeWidth={4} />
        <rect x={-300} y={92} width={600} height={18} rx={6} fill="#94a3b8" />
        {lines.map((ln, i) => (
          <text key={i} x={0} y={-(lines.length - 1) * lh / 2 + i * lh + 8} textAnchor="middle" fill={skin.textInk} fontSize={26} fontWeight={800} fontFamily={skin.fontFamily}>{ln}</text>
        ))}
      </g>
      {/* teacher presenting */}
      {skTeacher(150, 400, t, 1.6)}
    </g>
  );
};

export const SkinRecapScene: React.FC<SkinSceneProps> = ({ opacity = 1, t = 0, label = "Great job — you did it!" }) => {
  const skin = useSkin();
  const colors = ["#fb7185", "#fbbf24", "#34d399", "#60a5fa", "#a78bfa"];
  return (
    <g opacity={opacity}>
      <rect width={800} height={450} fill={skin.surface} />
      {/* falling confetti (deterministic) */}
      {Array.from({ length: 22 }, (_, i) => {
        const x = (i * 47 + 30) % 800;
        const fall = ((t * 60 + i * 53) % 480) - 30;
        const c = colors[i % colors.length];
        return i % 2 === 0
          ? <SkCircle key={i} cx={x} cy={fall} r={5} fill={c} seed={980 + i} />
          : <SkPolygon key={i} points={[[x, fall], [x + 8, fall + 4], [x, fall + 10], [x - 8, fall + 4]]} fill={c} seed={980 + i} />;
      })}
      {[180, 620].map((x, i) => <g key={i}>{skStar(x, 130, t, 0.8)}</g>)}
      <g transform="translate(400 180)">
        <rect x={-250} y={-30} width={500} height={60} rx={28} fill="rgba(255,255,255,0.92)" stroke="#cbd5e1" strokeWidth={2} />
        <text x={0} y={10} textAnchor="middle" fill={skin.textInk} fontSize={26} fontWeight={800} fontFamily={skin.fontFamily}>{label}</text>
      </g>
      {skPerson(400, 400, t, 1.8, true)}
    </g>
  );
};
